import { GTM, GTMFunctions } from "@/pixels/providers/gtm";
import { GOOGLE_DL_NAME } from "@/config/constants";
import config from "@/config/brands/index";
import * as packagesCodes from "@/config/constants/packagesCodes";
import store from "@/store";
import mock from "@/__mocks__/store-mock.js";
import * as utils from "@/utils";
import Vue from "vue";

jest.mock("@/store", () => ({
  state: {
    dataQuery: {
      camino: null
    },
    route: {
      name: null,
      meta: {
        config: {}
      }
    },
    paquetesBeneficios: {
      caracteristicas: {}
    },
    dataOferta: {}
  },
  getters: {
    getPaquetesBeneficios: () => ({ caracteristicas: {} })
  }
}));

global.window[GOOGLE_DL_NAME] = [];

describe("getMontoProducto (GTMFunctions)", () => {
  test("should return limite when paquete exists", () => {
    // Mock the get function to return the limite value
    jest.spyOn(utils, "get").mockReturnValueOnce(1000);
    const result = GTMFunctions.getMontoProducto();
    // The mocked store returns { caracteristicas: { limite: 1000 } }
    expect(result).toBe(1000);
  });

  test("should return undefined when no paquete exists", () => {
    const result = GTMFunctions.getMontoProducto();
    expect(result).toBe(undefined);
  });
});

describe("product data helpers (GTMFunctions)", () => {
  beforeEach(() => {
    store.state.dataQuery.camino = null;
    store.state.paquetesBeneficios.caracteristicas = {};
  });

  it("getProductData debe retornar vacío cuando no hay camino ni paquete", () => {
    expect(GTMFunctions.getProductData()).toEqual({
      name: "",
      type: ""
    });
  });

  it("commonEventParamsProductSegment debe retornar vacío para caminos distintos de 1 y 2", () => {
    store.state.dataQuery.camino = "3";

    expect(GTMFunctions.commonEventParamsProductSegment()).toEqual({});
  });

  it("getProductData debe usar camino 2 aunque no haya tipoPaquete", () => {
    store.state.dataQuery.camino = "2";
    store.state.paquetesBeneficios.caracteristicas = {};

    expect(GTMFunctions.getProductData()).toEqual({
      name: "",
      type: "paquete"
    });
  });

  it("getProductData debe usar el fallback vacío cuando nombresPaquetes no resuelve", () => {
    store.state.dataQuery.camino = "2";
    store.state.paquetesBeneficios.caracteristicas.tipoPaquete = "99";

    const namesSpy = jest.spyOn(packagesCodes, "nombresPaquetes");
    namesSpy.mockReturnValueOnce({
      toLocaleLowerCase: () => undefined
    });

    expect(GTMFunctions.getProductData()).toEqual({
      name: "",
      type: "paquete"
    });
  });
});

describe("contact data helpers (GTMFunctions)", () => {
  beforeEach(() => {
    store.state.consulta = {
      email: "",
      celular: {
        numero: "",
        normalizado: {
          caracteristica: "",
          nro: ""
        }
      }
    };
  });

  it("getContactData debe retornar email y phone normalizado", () => {
    store.state.consulta = {
      email: "customer@mail.com",
      celular: {
        numero: "1544556677",
        normalizado: {
          caracteristica: "11",
          nro: "12345678"
        }
      }
    };

    expect(GTMFunctions.getContactData()).toEqual({
      email: "customer@mail.com",
      phone: "1112345678"
    });
  });

  it("getHashedContactData debe hashear email y phone", async () => {
    const hashSpy = jest.spyOn(utils, "hashSHA256");
    hashSpy.mockResolvedValueOnce("hashed-email");
    hashSpy.mockResolvedValueOnce("hashed-phone");

    store.state.consulta = {
      email: "customer@mail.com",
      celular: {
        numero: "",
        normalizado: {
          caracteristica: "11",
          nro: "12345678"
        }
      }
    };

    await expect(GTMFunctions.getHashedContactData()).resolves.toEqual({
      email: "hashed-email",
      phone: "hashed-phone"
    });

    expect(hashSpy).toHaveBeenNthCalledWith(1, "customer@mail.com");
    expect(hashSpy).toHaveBeenNthCalledWith(2, "1112345678");
  });
});

describe("interaction helpers (GTMFunctions)", () => {
  it("commonEventParamsInteraction debe incluir application_typology y level3", () => {
    const result = GTMFunctions.commonEventParamsInteraction(
      "click",
      "oferta",
      "modal",
      "solicitud"
    );

    expect(result).toEqual(
      expect.objectContaining({
        application_interactionLevel: "click",
        page_pageInfo_level1: "nominas",
        page_pageInfo_level2: "oferta",
        page_pageInfo_level3: "modal",
        application_typology: "solicitud"
      })
    );
  });

  it("commonEventParamsInteraction debe usar level2 por defecto cuando no se envía", () => {
    const result = GTMFunctions.commonEventParamsInteraction("click");

    expect(result).toEqual(
      expect.objectContaining({
        application_interactionLevel: "click",
        page_pageInfo_level1: "nominas",
        page_pageInfo_level2: "undefined",
        page_pageInfo_pageName: "nominas :: undefined"
      })
    );
  });
});

describe("push helpers (GTMFunctions)", () => {
  const originalGoogleGtmId = config.googleGTMId;
  const originalGoogleGtmGA4 = config.googleGTMGA4;

  afterEach(() => {
    config.googleGTMId = originalGoogleGtmId;
    config.googleGTMGA4 = originalGoogleGtmGA4;
    global.window[GOOGLE_DL_NAME] = [];
  });

  it("enviar debe hacer push cuando GTM está habilitado", () => {
    config.googleGTMId = "enabled";
    config.googleGTMGA4 = true;
    delete global.window[GOOGLE_DL_NAME];
    const logSpy = jest.spyOn(Vue.$log, "info");

    GTMFunctions.enviar({
      eventName: "interaction_link",
      eventParams: {
        application_interactionLevel: "click"
      }
    });

    expect(global.window[GOOGLE_DL_NAME]).toHaveLength(1);
    expect(logSpy).toHaveBeenCalledWith(
      "GTM",
      expect.objectContaining({ event: "ga4.trackEvent" })
    );
  });

  it("enviar no debe hacer push cuando GTM está deshabilitado", () => {
    config.googleGTMId = "";
    config.googleGTMGA4 = true;
    global.window[GOOGLE_DL_NAME] = [];

    GTMFunctions.enviar({
      eventName: "interaction_link",
      eventParams: {
        application_interactionLevel: "click"
      }
    });

    expect(global.window[GOOGLE_DL_NAME]).toHaveLength(0);
  });

  it("enviar no debe duplicar el último interactionLevel", () => {
    config.googleGTMId = "enabled";
    config.googleGTMGA4 = true;
    global.window[GOOGLE_DL_NAME] = [
      {
        event: "ga4.trackEvent",
        eventParams: {
          application_interactionLevel: "click"
        }
      }
    ];

    GTMFunctions.enviar({
      eventName: "interaction_link",
      eventParams: {
        application_interactionLevel: "click"
      }
    });

    expect(global.window[GOOGLE_DL_NAME]).toHaveLength(1);
  });
});

describe("route and step helpers (GTMFunctions)", () => {
  it("getRouteName debe retornar el nombre actual de la ruta", () => {
    store.state.route.name = "mi-ruta";

    expect(GTMFunctions.getRouteName()).toBe("mi-ruta");
  });

  it("pageName sin argumentos debe retornar el nombre de la app", () => {
    expect(GTMFunctions.pageName()).toBe("nominas");
  });

  it("commonEventParamsAppStep debe usar pageName y application_type por defecto", () => {
    store.state.dataQuery.camino = "3";
    store.state.route.name = "gracias";

    const result = GTMFunctions.commonEventParamsAppStep(
      "step-x",
      undefined,
      "inicio"
    );

    expect(result).toEqual(
      expect.objectContaining({
        page_pageInfo_pageName: "nominas :: inicio :: gracias :: cliente",
        application_application_type: "solicitud",
        application_step: "step-x"
      })
    );
  });
});

describe("GTM config", () => {
  beforeEach(() => {
    global.window[GOOGLE_DL_NAME] = [];
    jest.spyOn(GTM, "enable");
    jest.spyOn(GTM, "wait");
    jest.spyOn(GTM, "init");
    jest.spyOn(GTM, "to_paso1");
    jest.spyOn(GTM, "to_paso2");
    jest.spyOn(GTM, "to_paso3");
    jest.spyOn(GTM, "to_paso4");
    jest.spyOn(GTM, "app_completed");
    jest.spyOn(GTM, "to_mensaje");
    store.state.dataQuery.camino = null;
    store.state.route.name = null;
    store.state.route.meta.config = {};
    store.state.dataOferta = {};
    store.state.paquetesBeneficios.caracteristicas = {};
    store.state.consulta = {
      email: "",
      celular: {
        numero: "",
        normalizado: {
          caracteristica: "",
          nro: ""
        }
      }
    };
    store.state.route.params = { mje: "error-1" };
  });

  it("configuracion GTM por defecto", () => {
    const eventData = {
      to: {
        meta: {
          config: {
            analyticsName: "test"
          }
        },
        params: {
          mje: ""
        }
      },
      name: "gtmTest",
      level2: "gtmTest",
      level3: "gtmTest",
      application_typology: "gtmTest"
    };

    GTM.enable();
    GTM.wait();
    GTM.init();
    GTM.to_paso1();
    GTM.to_paso2();
    GTM.to_paso3();
    GTM.to_paso4(eventData);
    GTM.app_completed();
    GTM.to_mensaje(eventData);

    const enableMock = jest.spyOn(GTM, "enable");
    const waitMock = jest.spyOn(GTM, "wait");
    const initMock = jest.spyOn(GTM, "init");
    const toPaso1Mock = jest.spyOn(GTM, "to_paso1");
    const toPaso2Mock = jest.spyOn(GTM, "to_paso2");
    const toPaso3Mock = jest.spyOn(GTM, "to_paso3");
    const toPaso4Mock = jest.spyOn(GTM, "to_paso4");
    const appCompletedMock = jest.spyOn(GTM, "app_completed");
    const toMensajeMock = jest.spyOn(GTM, "to_mensaje");

    expect(enableMock).toHaveBeenCalledTimes(1);
    expect(waitMock).toHaveBeenCalledTimes(1);
    expect(initMock).toHaveBeenCalledTimes(1);
    expect(toPaso1Mock).toHaveBeenCalledTimes(1);
    expect(toPaso2Mock).toHaveBeenCalledTimes(1);
    expect(toPaso3Mock).toHaveBeenCalledTimes(1);
    expect(toPaso4Mock).toHaveBeenCalledTimes(1);
    expect(appCompletedMock).toHaveBeenCalledTimes(1);
    expect(toMensajeMock).toHaveBeenCalledTimes(1);
  });

  it("configuracion exportada por defecto", () => {
    expect(GTM).toBeDefined();
    expect(GTM).not.toBeNull();
    expect(typeof GTM).toBe("object");
  });

  it("app_completed debe enviar productType = caja de ahorro en camino 1", async () => {
    store.state.dataQuery.camino = "1";
    await GTM.app_completed();
    expect(global.window[GOOGLE_DL_NAME]).toContainEqual({
      event: "ga4.trackEvent",
      eventName: "app_completed",
      eventParams: expect.objectContaining({
        products_attributes_0_productType: "caja de ahorro"
      })
    });
  });

  it("app_completed debe enviar productType = paquete en camino 2", async () => {
    store.getters = {
      getPaquetesBeneficios: () => mock.getters.getPaquetesBeneficios()
    };
    store.state.dataQuery.camino = "2";
    await GTM.app_completed();
    expect(global.window[GOOGLE_DL_NAME]).toContainEqual({
      event: "ga4.trackEvent",
      eventName: "app_completed",
      eventParams: expect.objectContaining({
        products_attributes_0_productType: "paquete"
      })
    });
  });
  it("app_completed debe enviar application_name = 'nominas :: cliente base' en camino 3", async () => {
    store.state.dataQuery.camino = "3";
    await GTM.app_completed();
    expect(global.window[GOOGLE_DL_NAME]).toContainEqual({
      event: "ga4.trackEvent",
      eventName: "app_completed",
      eventParams: expect.objectContaining({
        application_application_name: "nominas :: cliente base"
      })
    });
  });
  it("app_completed debe enviar operationNumberen routeName = thank", async () => {
    const hashSpy = jest.spyOn(utils, "hashSHA256");
    hashSpy.mockResolvedValueOnce("hashed-email");
    hashSpy.mockResolvedValueOnce("hashed-phone");

    store.state.route.name = "thank";
    store.state.dataOferta.nroDeTramite = "00011";
    store.state.consulta = {
      email: "test@mail.com",
      celular: {
        numero: "",
        normalizado: {
          caracteristica: "11",
          nro: "12345678"
        }
      }
    };

    await GTM.app_completed();

    expect(global.window[GOOGLE_DL_NAME]).toContainEqual({
      event: "ga4.trackEvent",
      eventName: "app_completed",
      eventParams: expect.objectContaining({
        email: "hashed-email",
        phone: "hashed-phone",
        application_operationNumber: "00011"
      })
    });
  });

  it("app_completed debe enviar email y phone hasheados", async () => {
    const hashSpy = jest.spyOn(utils, "hashSHA256");
    hashSpy.mockResolvedValueOnce("hashed-email-only");
    hashSpy.mockResolvedValueOnce("hashed-phone-only");

    store.state.route.name = "gracias";
    store.state.consulta = {
      email: "customer@mail.com",
      celular: {
        numero: "",
        normalizado: {
          caracteristica: "11",
          nro: "99887766"
        }
      }
    };

    await GTM.app_completed();

    expect(global.window[GOOGLE_DL_NAME]).toContainEqual(
      expect.objectContaining({
        event: "ga4.trackEvent",
        eventName: "app_completed",
        eventParams: expect.objectContaining({
          email: "hashed-email-only",
          phone: "hashed-phone-only"
        })
      })
    );
  });

  it("app_completed debe omitir operationNumber cuando la ruta no lo requiere", async () => {
    const hashSpy = jest.spyOn(utils, "hashSHA256");
    hashSpy.mockResolvedValueOnce("hashed-email-route");
    hashSpy.mockResolvedValueOnce("hashed-phone-route");

    store.state.route.name = "clienteCA";
    store.state.dataOferta.nroDeTramite = "00011";
    store.state.consulta = {
      email: "route@mail.com",
      celular: {
        numero: "",
        normalizado: {
          caracteristica: "11",
          nro: "12345678"
        }
      }
    };

    await GTM.app_completed();

    expect(global.window[GOOGLE_DL_NAME]).toContainEqual(
      expect.objectContaining({
        event: "ga4.trackEvent",
        eventName: "app_completed",
        eventParams: expect.objectContaining({
          email: "hashed-email-route",
          phone: "hashed-phone-route"
        })
      })
    );

    const lastEvent = global.window[GOOGLE_DL_NAME].slice(-1)[0];
    expect(lastEvent.eventParams.application_operationNumber).toBeUndefined();
  });

  it("commonEventParamsAppError debe incluir level3 cuando se proporciona", () => {
    const result = GTMFunctions.commonEventParamsAppError(
      "error tecnico",
      "error general",
      "modal"
    );

    expect(result).toEqual(
      expect.objectContaining({
        page_pageInfo_pageName: "nominas :: error tecnico :: modal",
        page_pageInfo_level1: "nominas",
        page_pageInfo_level2: "error tecnico",
        page_pageInfo_errorPage: "error general",
        page_pageInfo_level3: "modal"
      })
    );
  });

  it("to_paso4 no debe enviar evento si analyticsName está vacío", () => {
    const sendSpy = jest.spyOn(GTMFunctions, "enviar");

    GTM.to_paso4({
      to: {
        meta: {
          config: {
            analyticsName: ""
          }
        }
      }
    });

    expect(sendSpy).not.toHaveBeenCalled();
  });
  it("to_mensaje debe enviar analyticsModule = error tecnico en error-1", () => {
    const eventData = {
      to: {
        params: {
          mje: "error-1"
        }
      }
    };

    GTM.to_mensaje(eventData);

    expect(global.window[GOOGLE_DL_NAME]).toContainEqual(
      expect.objectContaining({
        event: "ga4.trackEvent",
        eventName: "server_error",
        eventParams: expect.objectContaining({
          page_pageInfo_errorPage: "error tecnico"
        })
      })
    );
  });

  it("to_mensaje debe enviar analyticsModule = error tecnico en error-2", () => {
    const eventData = {
      to: {
        params: {
          mje: "error-2"
        }
      }
    };

    GTM.to_mensaje(eventData);

    expect(global.window[GOOGLE_DL_NAME]).toContainEqual(
      expect.objectContaining({
        event: "ga4.trackEvent",
        eventName: "server_error",
        eventParams: expect.objectContaining({
          page_pageInfo_errorPage: "individuo negativo"
        })
      })
    );
  });

  it("to_mensaje debe enviar analyticsModule = error tecnico en error-3", () => {
    const eventData = {
      to: {
        params: {
          mje: "error-3"
        }
      }
    };

    GTM.to_mensaje(eventData);

    expect(global.window[GOOGLE_DL_NAME]).toContainEqual(
      expect.objectContaining({
        event: "ga4.trackEvent",
        eventName: "server_error",
        eventParams: expect.objectContaining({
          page_pageInfo_errorPage: "rechazado"
        })
      })
    );
  });

  it("interaction_link debe enviar eventName interaction_link", () => {
    GTM.interaction_link({
      name: "continuar",
      level2: "oferta",
      level3: "modal"
    });

    expect(global.window[GOOGLE_DL_NAME]).toContainEqual(
      expect.objectContaining({
        event: "ga4.trackEvent",
        eventName: "interaction_link",
        eventParams: expect.objectContaining({
          application_interactionLevel: "continuar",
          page_pageInfo_level2: "oferta",
          page_pageInfo_level3: "modal"
        })
      })
    );
  });

  it("to_oferta debe hashear email y phone desde store.consulta", async () => {
    const hashSpy = jest.spyOn(utils, "hashSHA256");
    hashSpy.mockResolvedValueOnce("hashed-email");
    hashSpy.mockResolvedValueOnce("hashed-phone");
    hashSpy.mockClear();

    store.state.consulta = {
      email: "test@mail.com",
      celular: {
        numero: "",
        normalizado: {
          caracteristica: "11",
          nro: "12345678"
        }
      }
    };

    await GTM.to_oferta();

    expect(hashSpy).toHaveBeenNthCalledWith(1, "test@mail.com");
    expect(hashSpy).toHaveBeenNthCalledWith(2, "1112345678");

    expect(global.window[GOOGLE_DL_NAME]).toContainEqual(
      expect.objectContaining({
        event: "ga4.trackEvent",
        eventName: "app_step_0",
        eventParams: expect.objectContaining({
          email: "hashed-email",
          phone: "hashed-phone"
        })
      })
    );
  });

  it("to_oferta debe usar celular.numero como fallback cuando no hay normalizado", async () => {
    const hashSpy = jest.spyOn(utils, "hashSHA256");
    hashSpy.mockResolvedValueOnce("hashed-email");
    hashSpy.mockResolvedValueOnce("hashed-phone-numero");
    hashSpy.mockClear();

    store.state.consulta = {
      email: "test2@mail.com",
      celular: {
        numero: "1544556677",
        normalizado: {
          caracteristica: "",
          nro: ""
        }
      }
    };

    await GTM.to_oferta();

    expect(hashSpy).toHaveBeenNthCalledWith(1, "test2@mail.com");
    expect(hashSpy).toHaveBeenNthCalledWith(2, "1544556677");
  });
});
